import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, MapPin, Clock3, Users } from "lucide-react";
import { useSiteContent, useSiteCopy, assetUrl } from "@/lib/siteContent";

export default function About() {
  const { tx } = useSiteCopy();
  const { about, headings, business } = useSiteContent();
  const points = about.points;
  const [imgSrc, setImgSrc] = useState(assetUrl(about.image));
  useEffect(() => setImgSrc(assetUrl(about.image)), [about.image]);

  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32 relative overflow-hidden">
      <div
        className="absolute top-1/3 -left-40 w-[500px] h-[500px] rounded-full opacity-10 blur-3xl"
        style={{ background: "radial-gradient(circle, #00E5FF 0%, transparent 65%)" }}
        aria-hidden="true"
      />
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <div className="absolute -inset-4 border border-electric/30 rounded-3xl translate-x-4 translate-y-4" aria-hidden="true" />
          <img
            src={imgSrc}
            onError={() => setImgSrc("/assets/emblem.webp")}
            alt={about.image_alt}
            data-testid="about-image"
            className="relative rounded-3xl object-contain bg-navy w-full aspect-[6/5] shadow-2xl"
          />
          <div className="absolute -bottom-6 -right-4 md:right-8 metal-card rounded-2xl px-6 py-4 glow-blue" data-testid="about-badge">
            <p className="font-bebas text-3xl text-electric tracking-wider">{tx("about-badge", "24/7")}</p>
            <p className="text-xs text-slate-400 uppercase tracking-widest">{tx("about-badge-description", "Emergency Service")}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ y: 40, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-electric font-semibold tracking-[0.3em] text-sm uppercase mb-4" data-testid="about-eyebrow">{headings.about.eyebrow}</p>
          <h2 className="font-bebas text-4xl md:text-6xl tracking-tight chrome-text leading-none">
            {headings.about.title}
          </h2>
          <p className="mt-6 text-slate-400 font-light leading-relaxed">
            {headings.about.description}
          </p>

          <ul className="mt-8 space-y-4" data-testid="about-checklist">
            {points.map((p) => (
              <li key={p} className="flex items-start gap-3 text-slate-200">
                <CheckCircle2 className="w-5 h-5 text-electric shrink-0 mt-0.5" />
                <span className="font-light">{p}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-wrap gap-8 text-sm text-slate-400">
            <span className="flex items-center gap-2" data-testid="about-location">
              <MapPin className="w-4 h-4 text-electric" /> {business.region}
            </span>
            <span className="flex items-center gap-2">
              <Clock3 className="w-4 h-4 text-electric" /> {tx("about-emergency", "24/7 Emergency")}
            </span>
            <span className="flex items-center gap-2">
              <Users className="w-4 h-4 text-electric" /> {tx("about-family", "Family Owned")}
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
